import AnnouncementService from '@/services/v2/intranet/announcement.service';

export const announcementApi = {
  namespaced: true,
  state: {
    announcements: [],
    isLoading: false,
    isPending: false,
    error: null,
    fetchError: null,
    isAnnouncementAdmin: false,
  },
  getters: {
    getAnnouncements: (state) => state.announcements,
    isLoading: (state) => state.isLoading,
    isPending: (state) => state.isPending,
    isError: (state) => state.error !== null,
    getError: (state) => state.error,
    isFetchError: (state) => state.fetchError !== null,
    getFetchError: (state) => state.fetchError,
    isAdmin: (state) => state.isAnnouncementAdmin,
  },
  actions: {
    fetchAnnouncements({ commit }) {
      commit('setIsLoading', true);
      commit('clearFetchError');

      return AnnouncementService.getAnnouncements().then(
        (resp) => {
          commit('setAnnouncements', resp.data);
          commit('setIsLoading', false);
          commit('dummyConsole', 'AnnouncementService.getAnnouncements - success');
          return Promise.resolve(resp);
        },
        (error) => {
          commit('setFetchError', error.message || 'Failed to fetch announcements');
          commit('setIsLoading', false);
          commit('dummyConsole', 'AnnouncementService.getAnnouncements - error');
          return Promise.reject(error);
        }
      );
    },
    fetchAnnouncementById({ commit, dispatch }, announcementId) {
      commit('setIsLoading', true);
      commit('clearError');

      return AnnouncementService.getAnnouncementById(announcementId).then(
        (resp) => {
          commit('setAnnouncementById', resp.data);
          commit('setIsLoading', false);
          commit('dummyConsole', 'AnnouncementService.getAnnouncementById - success');
          return Promise.resolve(resp);
        },
        (error) => {
          const errorMessage = error.message || 'Failed to fetch announcement by id';
          commit('setError', errorMessage);
          commit('setIsLoading', false);
          dispatch('statusSnackbar/showError', error, { root: true });
          commit('dummyConsole', 'AnnouncementService.getAnnouncementById - error');
          return Promise.reject(error);
        }
      );
    },
    createAnnouncement({ commit, dispatch }, announcement) {
      commit('setIsPending', true);
      commit('clearError');

      return AnnouncementService.createAnnouncement(announcement).then(
        (resp) => {
          commit('dummyConsole', 'AnnouncementService.createAnnouncement - success');
          // Refetch announcements after creation
          dispatch('fetchAnnouncements').then(() => {
            commit('setIsPending', false);
          });
          return Promise.resolve(resp);
        },
        (error) => {
          const errorMessage = error.message || 'Failed to create announcement';
          commit('setError', errorMessage);
          commit('setIsPending', false);
          dispatch('statusSnackbar/showError', error, { root: true });
          commit('dummyConsole', 'AnnouncementService.createAnnouncement - error');
          return Promise.reject(error);
        }
      );
    },
    updateAnnouncement({ commit, dispatch }, { announcementId, announcement }) {
      commit('setIsPending', true);
      commit('clearError');

      return AnnouncementService.updateAnnouncement(announcementId, announcement).then(
        (resp) => {
          commit('dummyConsole', 'AnnouncementService.updateAnnouncement - success');
          // Refetch announcements after update
          dispatch('fetchAnnouncements').then(() => {
            commit('setIsPending', false);
          });
          return Promise.resolve(resp);
        },
        (error) => {
          const errorMessage = error.message || 'Failed to update announcement';
          commit('setError', errorMessage);
          commit('setIsPending', false);
          dispatch('statusSnackbar/showError', error, { root: true });
          commit('dummyConsole', 'AnnouncementService.updateAnnouncement - error');
          return Promise.reject(error);
        }
      );
    },
    deleteAnnouncement({ commit, dispatch }, announcementId) {
      commit('setIsPending', true);
      commit('clearError');

      return AnnouncementService.deleteAnnouncement(announcementId).then(
        (resp) => {
          commit('dummyConsole', 'AnnouncementService.deleteAnnouncement - success');
          // Refetch announcements after deletion
          dispatch('fetchAnnouncements').then(() => {
            commit('setIsPending', false);
          });
          return Promise.resolve(resp);
        },
        (error) => {
          const errorMessage = error.message || 'Failed to delete announcement';
          commit('setError', errorMessage);
          commit('setIsPending', false);
          dispatch('statusSnackbar/showError', error, { root: true });
          commit('dummyConsole', 'AnnouncementService.deleteAnnouncement - error');
          return Promise.reject(error);
        }
      );
    },
    downloadAttachment({ commit, dispatch }, fileKey) {
      commit('setIsPending', true);

      return AnnouncementService.downloadAttachment(fileKey).then(
        (resp) => {
          commit('setIsPending', false);
          commit('dummyConsole', 'AnnouncementService.downloadAttachment - success');
          return Promise.resolve(resp);
        },
        (error) => {
          commit('setIsPending', false);
          dispatch('statusSnackbar/showError', error, { root: true });
          commit('dummyConsole', 'AnnouncementService.downloadAttachment - error');
          return Promise.reject(error);
        }
      );
    },
    checkAnnouncementAdmin({ commit }) {
      commit('setIsPending', true);
      commit('clearError');

      return AnnouncementService.checkAnnouncementAdmin().then(
        (resp) => {
          commit('setIsAnnouncementAdmin', true);
          commit('setIsPending', false);
          return Promise.resolve(resp);
        },
        (error) => {
          const errorMessage = error.message || 'Failed to check announcement admin';
          commit('setError', errorMessage);
          commit('setIsPending', false);
          commit('setIsAnnouncementAdmin', false);
          return Promise.reject(error);
        }
      );
    },
  },
  mutations: {
    setAnnouncements(state, announcements) {
      state.announcements = announcements;
    },
    setAnnouncementById(state, announcement) {
      // This is used when fetching by ID
      const index = state.announcements.findIndex(
        (a) => a.announcementId === announcement.announcementId
      );
      if (index === -1) {
        state.announcements.push(announcement);
      } else {
        state.announcements.splice(index, 1, announcement);
      }
    },
    setIsPending(state, pending) {
      state.isPending = pending;
    },
    setIsLoading(state, loading) {
      state.isLoading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    setFetchError(state, error) {
      state.fetchError = error;
    },
    clearFetchError(state) {
      state.fetchError = null;
    },
    clearError(state) {
      state.error = null;
    },
    setIsAnnouncementAdmin(state, isAnnouncementAdmin) {
      state.isAnnouncementAdmin = isAnnouncementAdmin;
    },
    dummyConsole(state, obj) {
      console.log(obj);
    },
  },
};

export default announcementApi;
